import { yupResolver } from '@hookform/resolvers';
import { AuthLayout } from '@ifca-root/react-component/src/components/AuthLayout/AuthLayout';
import { CopyRight } from '@ifca-root/react-component/src/components/AuthLayout/CopyRight';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import SnackBarMsg from '@ifca-root/react-component/src/components/SnackBar/SnackBarMsg';
import { smallTitle } from '@ifca-root/react-component/src/helpers/Global/ContractGlobalVar';
import { CX_VERSION } from '@ifca-root/react-component/src/helpers/Global/ContractVersion';
import { contractEnvVar } from '@ifca-root/react-component/src/helpers/Global/EnvironmentVar';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { setAccessToken } from 'AccessToken';
import Background from 'assets/images/App-Background.jpg';
import Logo from 'assets/images/contract-x-logo.png';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import { ContractClient } from 'ContractClient';
import {
  LoggedInUserProfileDocument,
  LoggedInUserProfileQuery,
  useCreateUpdateUserActivityLogMutation,
  useLoginMutation,
} from 'generated/graphql';
import {
  isRestorablePath,
  RestoreState,
  SessionExpiredState,
} from 'helpers/AuthRedirect';
import { encryptMessage } from 'helpers/Crypto/JSEncrypt';
import { LoginSchema } from 'helpers/YupSchema/formSchema';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

interface ILoginForm {
  email: string;
  password: string;
  showPassword: boolean;
  rememberMe: boolean;
  product: string;
}

export const Login = (props: any) => {
  let history = useHistory();
  const errState = useLocation<SessionExpiredState | null>();
  const rememberme: any = localStorage.getItem('rememberme');
  const { setSnackBarMsg, setOpenSnackBar }: any = useContext(
    SnackBarContext as any,
  );

  // frozen on first render; errState.state is nulled below and onCompleted is recreated per render
  const restoreStateRef = useRef<Omit<SessionExpiredState, 'sessionExpired'>>({
    from: errState?.state?.from,
    companyID: errState?.state?.companyID,
    userID: errState?.state?.userID,
  });
  const sessionExpired = !!errState?.state?.sessionExpired;

  if (!!errState?.state) {
    errState.state = null;
  }
  if (sessionExpired) {
    setSnackBarMsg(
      'Session has Expired/Not Authenticated! Please relogin again',
    );
    setOpenSnackBar(true);
  }

  // DECLARE TITLE HEADER
  useEffect(() => {
    document.title = `${smallTitle.PRODUCT} ${contractEnvVar()}`;
  }, []);

  const [loginAccount, { loading }] = useLoginMutation({
    client: ContractClient as any,
    onError: error => {
      console.log(error, 'eroror');
      error.graphQLErrors.map(({ message }) => {
        console.log('ERROR', message);
        setMessage(message);
        setOpenAlert(true);
      });
    },
    update: (store, { data }) => {
      if (!data) return null;
      store.writeQuery<LoggedInUserProfileQuery>({
        query: LoggedInUserProfileDocument,
        data: {
          __typename: 'Query',
          loggedInUserProfile: data.login.user as any,
        },
      });
    },
    onCompleted: ({ login }) => {
      localStorage.clear();
      if (rememberMe) {
        localStorage.setItem(
          'rememberme',
          JSON.stringify({
            username: getValues('email'),
            password: getValues('password'),
          }),
        );
      }
      if (!!login && !!login?.accessToken) {
        setAccessToken(login?.accessToken);
        sessionStorage.setItem('accessToken', login?.accessToken);
      }
      if (!!login?.user && !!login?.user?.lastestAccessedEntity) {
        localStorage.setItem('companyID', login?.user?.lastestAccessedEntity);
      }
      createLog({ variables: { userAction: 'LOGIN' } });
      const { from, companyID, userID } = restoreStateRef.current;
      if (isRestorablePath(from)) {
        const restoreState: RestoreState = { redirectTo: from, companyID, userID };
        history.push('/', restoreState);
      } else {
        history.push('/');
      }
    },
  });

  const [
    createLog,
    { loading: createLogLoading },
  ] = useCreateUpdateUserActivityLogMutation({
    client: ContractClient as any,
    onError: error => {
      error.graphQLErrors.map(({ message }) => {
        console.log('ERROR', message);
        setMessage(message);
        setOpenAlert(true);
      });
    },
  });

  const [rememberMe, setRememberMe] = useState(
    rememberme !== null ? true : false,
  );
  const [values, setValues] = useState(false);
  const [open, setOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [message, setMessage] = useState('');

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleCloseSnackBar = () => {
    setOpen(false);
    history.push('/profile');
  };
  const MESSAGE = 'Password Changed Successfully';

  const handleClickShowPassword = () => {
    setValues(!values);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };
  const handleCheckRememberMe = (checked: boolean) => {
    setRememberMe(checked);
  };

  //useForm set DefaultValues, validationSchema
  const { handleSubmit, register, errors, control, getValues, reset } = useForm<
    ILoginForm
  >({
    defaultValues: {
      email: rememberme !== null ? JSON.parse(rememberme)?.username : '',
      password: rememberme !== null ? JSON.parse(rememberme)?.password : '',
    },
    resolver: yupResolver(LoginSchema),
  });

  // NEW WITH ENCRYPTED MESSAGE FOR PASSWORD
  const onSubmit = data => {
    loginAccount({
      variables: {
        loginId: data?.email,
        password: encryptMessage(data?.password),
      },
    });
  };

  return (
    <>
      {loading && <Loading />}
      {createLogLoading && <Loading />}
      <AuthLayout className="margin-override" logo={Logo} image={Background}>
        <div
          className="xsTitle color-primary-orange"
          style={{ textAlignLast: 'center' }}
        >
          {CX_VERSION}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} id="submit-form">
          <div className="form-box">
            <Controller
              as={TextField}
              fullWidth
              helperText={errors?.email?.message}
              error={errors?.email ? true : false}
              autoComplete="off"
              name="email"
              label="Email/Username"
              control={control}
              data-qa="usernameInput"
            />
            <Grid container>
              <Controller
                as={TextField}
                fullWidth
                helperText={errors?.password?.message}
                error={errors?.password ? true : false}
                autoComplete="off"
                name="password"
                label="Password "
                type={values ? 'text' : 'password'}
                value={getValues('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Password"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {values ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                control={control}
                data-qa="passwordInput"
              />
            </Grid>

            <div className="other-auth">
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkedB"
                    color="primary"
                    checked={rememberMe}
                    onChange={e => handleCheckRememberMe(e.target.checked)}
                  />
                }
                label="Remember Me"
              />
              <span
                className="forget-link"
                onClick={() => history.push('/forgot-password')}
                data-qa="forgotPasswordLink"
              >
                Forgot Password?
              </span>
            </div>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="login-btn"
              data-qa="loginButton"
            >
              Login
            </Button>
          </div>
        </form>
        <CommonDialog
          fullWidth={true}
          open={openAlert}
          onClose={handleCloseAlert}
          sections={{
            header: { title: 'Login Error' },
            body: () => <div>{message}</div>,
            footer: {
              actions: [
                {
                  displayText: 'Ok',
                  props: {
                    onClick: () => handleCloseAlert(),
                    variant: 'contained',
                    color: 'primary',
                  },
                },
              ],
            },
          }}
        />
        <SnackBarMsg
          open={open}
          message={MESSAGE}
          onClose={handleCloseSnackBar}
        />
      </AuthLayout>

      <CopyRight />
    </>
  );
};
